import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    Output,
    EventEmitter,
    Input,
    Renderer2
} from '@angular/core';

import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/internal/Subject';
import { Currency, StudentAccount, Teacher } from '@df/interfaces';
import {
    COMMENTS,
    DECREASE_OPERATION,
    GENDER,
    INCREASE_OPERATION,
    RESET_OPERATION,
    REWARD_BANK_TYPES
} from '@app/app.constants';
import { AccountService } from '@app/teacher/services/account.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';

@Component({
    selector: 'app-account-actions',
    templateUrl: './account-actions.component.html',
    styleUrls: ['./account-actions.component.scss']
})
export class AccountActionsComponent implements OnInit, OnDestroy {
    @Input() selectedStudent: StudentAccount;
    @Input() currency: Currency;

    studentAccount: StudentAccount;
    today = new Date();
    studentAmount: number = 0;
    bankActionsForm: FormGroup;
    currentUser: { first_name: string; last_name: string; is_teacher: boolean };
    teacher: Teacher;
    reward_active = REWARD_BANK_TYPES.DRAHMA; // kairos, stars, coins

    @Output() changeSumReward = new EventEmitter<any>();
    @Output() changeStudentReward = new EventEmitter<number>();

    @ViewChild('amountInput', { static: true }) input: ElementRef;
    @ViewChild('amountPlaceholder', { static: true }) placeholder: ElementRef;

    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(private accountService: AccountService, private renderer2: Renderer2) {}

    public ngOnInit(): void {
        // console.log(this.currency);
        this.initCurrentUser();
        this.initSelectedStudent();
        this.initBankForm();
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
    }

    private initBankForm(): void {
        this.bankActionsForm = new FormGroup({
            amount: new FormControl(null, [
                Validators.required,
                Validators.min(1),
                Validators.pattern(/^[1-9]+[0-9]*$/)
            ])
        });
    }

    private initSelectedStudent(): void {
        this.accountService.studentSelected.subscribe((student: StudentAccount) => {
            this.selectedStudent = student;
            this.reward_active = REWARD_BANK_TYPES.DRAHMA;
            // console.log(this.selectedStudent);
            this.accountService
                .getStudentAccount(this.selectedStudent.id)
                .pipe(takeUntil(this.destroy$))
                .subscribe((studentAccount: StudentAccount) => {
                    this.studentAccount = studentAccount;
                    this.studentAmount = +this.studentAccount.amount;
                    this.updateStudentAmount(this.studentAccount);
                });
        });
    }

    private initCurrentUser(): void {
        this.accountService
            .getCurrentUser()
            .pipe(takeUntil(this.destroy$))
            .subscribe((resp: any) => {
                this.currentUser = resp.data;
                // console.log(this.currentUser);
                if (this.currentUser.is_teacher) {
                    this.initTeacher();
                }
            });
    }

    private initTeacher() {
        this.accountService
            .getCurrentTeacher()
            .pipe(takeUntil(this.destroy$))
            .subscribe((teacher: Teacher) => {
                this.teacher = teacher;
            });
    }

    private getTransactionComment(operation: string): string {
        const userName = `${this.currentUser.first_name} ${this.currentUser.last_name}`;
        let comment = `${COMMENTS.CASH_IN} (${userName})`;

        if (operation === DECREASE_OPERATION) {
            comment = `${COMMENTS.CASH_OUT} (${userName})`;
        }

        if (operation === RESET_OPERATION) {
            comment = `${COMMENTS.CASH_RESET} (${userName})`;
        }

        if (this.teacher) {
            if (this.teacher.user.gender === GENDER.MALE) {
                comment = `${userName} ${COMMENTS.CASH_IN_MALE}`;
                if (operation === DECREASE_OPERATION) {
                    comment = `${userName} ${COMMENTS.CASH_OUT_MALE}`;
                }
                if (operation === RESET_OPERATION) {
                    comment = `${userName} ${COMMENTS.CASH_RESET_MALE}`;
                }
            } else if (this.teacher.user.gender === GENDER.FEMALE) {
                comment = `${userName} ${COMMENTS.CASH_IN_FEMALE}`;
                if (operation === DECREASE_OPERATION) {
                    comment = `${userName} ${COMMENTS.CASH_OUT_FEMALE}`;
                }
                if (operation === RESET_OPERATION) {
                    comment = `${userName} ${COMMENTS.CASH_RESET_FEMALE}`;
                }
            }
        }

        return comment;
    }

    private submitOperation(operation: string, comment: string = ''): void {
        // console.log(operation, comment);
        const data = {
            amount: +this.bankActionsForm.get('amount').value,
            operation,
            comment,
            rewardType: this.reward_active
        };
        console.log(data);
        this.accountService
            .accountOperation(this.studentAccount.student, data)
            .pipe(takeUntil(this.destroy$))
            .subscribe((studentAccount: StudentAccount) => {
                console.log(studentAccount);
                this.fetchOperation(studentAccount);
            });
    }

    private fetchOperation(studentAccount: StudentAccount): void {
        this.studentAccount = studentAccount;
        this.bankActionsForm.patchValue({
            amount: null
        });
        this.onInputBlur();
        this.updateStudentAmount(studentAccount);
        this.accountService.updateStudentAccount(this.studentAccount);
    }

    private updateStudentAmount(studentAccount: StudentAccount): void {
        this.changeSumReward.emit();
        this.studentAmount = +studentAccount.amount;

        if (this.reward_active === REWARD_BANK_TYPES.STARS) {
            this.studentAmount = studentAccount.profile.stars;
        }
        if (this.reward_active === REWARD_BANK_TYPES.KAIROS) {
            this.studentAmount = studentAccount.profile.kairos;
        }
        this.changeStudentReward.emit(this.studentAmount);
    }

    public addToAccount(): void {
        const comment = this.getTransactionComment(INCREASE_OPERATION);
        this.submitOperation(INCREASE_OPERATION, comment);
    }

    public removeFromAccount(): void {
        const comment = this.getTransactionComment(DECREASE_OPERATION);
        this.submitOperation(DECREASE_OPERATION, comment);
    }

    public resetAccount(): void {
        const comment = this.getTransactionComment(RESET_OPERATION);

        this.submitOperation(RESET_OPERATION, comment);
    }

    getRewardColor(): string {
        if (this.reward_active === REWARD_BANK_TYPES.DRAHMA) {
            return '#6795c5';
        }
        if (this.reward_active === REWARD_BANK_TYPES.STARS) {
            return '#ff3d6a';
        }
        if (this.reward_active === REWARD_BANK_TYPES.KAIROS) {
            return '#1199a8';
        }
    }

    public onInputFocus(): void {
        this.renderer2.addClass(this.placeholder.nativeElement, 'hide');
        this.renderer2.removeClass(this.input.nativeElement, 'untouched');
    }

    public onInputBlur(): void {
        const value = this.bankActionsForm.get('amount').value;
        if (value === null) {
            this.renderer2.removeClass(this.placeholder.nativeElement, 'hide');
            this.renderer2.addClass(this.input.nativeElement, 'untouched');
        }
    }
}
